import React, { useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";

import arrowIcon from "../../assets/svg/arrow-s.svg";
import { getInstructorReservationHistory } from "../../api/reservationsService";
import { openSubPopup } from "../Common/popupUIStore";

const DELETE_ACTIONS = new Set(["DELETE", "DELETED", "REMOVE", "REMOVED"]);
const CREATE_ACTIONS = new Set(["CREATE", "CREATED"]);
const CANCEL_ACTIONS = new Set(["CANCEL", "CANCELLED", "CANCELED"]);
const UPDATE_ACTIONS = new Set([
   "UPDATE",
   "UPDATED",
   "MODIFY",
   "MODIFIED",
   "PATCH",
   "EDIT",
   "EDITED",
]);
const MOLDOVA_TZ_ID = "Europe/Chisinau";
const RANGE_MINUTES = 90;

const HISTORY_FIELD_LABELS = {
   startTime: "Data/ora",
   start_time: "Data/ora",
   start: "Data/ora",
   userId: "Cursant",
   studentId: "Cursant",
   instructorId: "Instructor",
   sector: "Sector",
   gearbox: "Cutie",
   privateMessage: "Notă",
   private_message: "Notă",
   note: "Notă",
   color: "Culoare",
   isFavorite: "Favorit",
   is_favorite: "Favorit",
   isImportant: "Important",
   is_important: "Important",
   isCancelled: "Anulat",
   is_cancelled: "Anulat",
   isConfirmed: "Confirmare",
   is_confirmed: "Confirmare",
};

function safeText(value) {
   if (value == null) return "";
   return String(value).trim();
}

function toTimeMs(value) {
   if (value == null) return 0;
   if (value instanceof Date) {
      const ms = value.getTime();
      return Number.isFinite(ms) ? ms : 0;
   }
   const ms = Date.parse(String(value));
   return Number.isFinite(ms) ? ms : 0;
}

function addMinutes(dateLike, minutes) {
   const base = dateLike instanceof Date ? dateLike : parseDateFlexible(dateLike);
   if (!(base instanceof Date) || Number.isNaN(base.getTime())) return null;
   const mm = Number(minutes);
   if (!Number.isFinite(mm)) return null;
   return new Date(base.getTime() + mm * 60 * 1000);
}

function toBoolMaybe(value) {
   if (value === true || value === false) return value;
   if (value == null) return null;
   if (value === 1 || value === "1") return true;
   if (value === 0 || value === "0") return false;
   const text = String(value).trim().toLowerCase();
   if (!text) return null;
   if (text === "true" || text === "da" || text === "yes") return true;
   if (text === "false" || text === "nu" || text === "no") return false;
   return null;
}

function parseDateFlexible(value) {
   if (value == null) return null;
   if (value instanceof Date) {
      const ms = value.getTime();
      return Number.isFinite(ms) ? new Date(ms) : null;
   }

   const raw = String(value).trim();
   if (!raw) return null;

   const parsed = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s]+(\d{2}):(\d{2})(?::(\d{2}))?)?/,
   );
   if (parsed) {
      // În istoricul rezervărilor putem primi timpuri marcate cu offset/Z
      // care reprezintă deja ora locală de slot. Le tratăm ca "floating"
      // ca să evităm deplasarea orei la compararea pe casetă.
      const [, year, month, day, hour = "00", minute = "00", second = "00"] =
         parsed;
      const date = new Date(
         Number(year),
         Number(month) - 1,
         Number(day),
         Number(hour),
         Number(minute),
         Number(second),
         0,
      );
      return Number.isNaN(date.getTime()) ? null : date;
   }

   const nativeDate = new Date(raw);
   if (!Number.isNaN(nativeDate.getTime())) return nativeDate;
   return null;
}

function readRawDateTimeParts(value) {
   const text = String(value || "").trim();
   if (!text) return null;

   const match = text.match(
      /(\d{4})-(\d{2})-(\d{2})(?:[T\s]+)(\d{2}):(\d{2})/,
   );
   if (!match) return null;

   const [, year, month, day, hour, minute] = match;
   return { year, month, day, hour, minute };
}

function buildLocalIsoTextFromDate(dateLike) {
   const d = parseDateFlexible(dateLike);
   if (!d) return "";
   const year = String(d.getFullYear()).padStart(4, "0");
   const month = String(d.getMonth() + 1).padStart(2, "0");
   const day = String(d.getDate()).padStart(2, "0");
   const hour = String(d.getHours()).padStart(2, "0");
   const minute = String(d.getMinutes()).padStart(2, "0");
   return `${year}-${month}-${day}T${hour}:${minute}:00`;
}

function fmtDate(value) {
   if (!value) return "—";
   const raw = readRawDateTimeParts(value);
   if (raw) return `${raw.day}.${raw.month}.${raw.year}`;

   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return String(value);
   return d.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
   });
}

function fmtTime(value) {
   if (!value) return "—";
   const raw = readRawDateTimeParts(value);
   if (raw) return `${raw.hour}:${raw.minute}`;

   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return "—";
   return d.toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
   });
}

function normalizeColorToCss(value, fallback = "var(--event-default)") {
   const raw = String(value || "").trim();
   if (!raw) return fallback;
   if (raw.startsWith("var(")) return raw;

   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      if (!short || short === "default") return "var(--event-default)";
      if (short.startsWith("event-")) return `var(--${short})`;
      return `var(--event-${short})`;
   }

   if (/^event-/i.test(raw)) return `var(--${raw.toLowerCase()})`;
   return raw;
}

function fmtDateTimeRO(value) {
   if (!value) return "—";
   const raw = readRawDateTimeParts(value);
   if (raw) return `${raw.day}.${raw.month}.${raw.year}, ${raw.hour}:${raw.minute}`;

   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return String(value);
   return d.toLocaleString("ro-RO", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: MOLDOVA_TZ_ID,
   });
}

function formatHistoryFieldLabel(field) {
   const raw = safeText(field);
   if (!raw) return "";
   return HISTORY_FIELD_LABELS[raw] || HISTORY_FIELD_LABELS[raw.toLowerCase()] || raw;
}

function summarizeHistoryChanges(item) {
   if (!item || typeof item !== "object") return "";

   const labels = [];
   const pushLabel = (field) => {
      const label = formatHistoryFieldLabel(field);
      if (!label) return;
      const key = label.toLowerCase();
      if (!labels.some((entry) => entry.toLowerCase() === key)) labels.push(label);
   };

   if (item.changedFields && typeof item.changedFields === "object") {
      for (const field of Object.keys(item.changedFields)) {
         const rawField = String(field || "").trim().toLowerCase();
         if (
            rawField === "action" ||
            rawField === "newdata" ||
            rawField === "olddata" ||
            rawField === "metadata" ||
            rawField === "snapshot" ||
            rawField === "reservation" ||
            rawField === "data"
         ) {
            continue;
         }
         pushLabel(field);
      }
   }

   if (Array.isArray(item.changes)) {
      for (const change of item.changes) {
         pushLabel(change?.field || change?.path || "");
      }
   }

   return labels.join(", ");
}

function formatActionLabel(action, item) {
   const normalized = safeText(action).toUpperCase();
   if (CREATE_ACTIONS.has(normalized)) return "Creat";
   if (DELETE_ACTIONS.has(normalized)) return "Șters";
   if (CANCEL_ACTIONS.has(normalized)) return "Anulat";
   if (normalized === "CONFIRM" || normalized === "CONFIRMED") return "Confirmat";
   if (UPDATE_ACTIONS.has(normalized)) return "Modificat";

   const changesSummary = summarizeHistoryChanges(item);
   if (changesSummary) return "Modificat";
   if (!normalized) return "Modificat";
   return normalized.charAt(0) + normalized.slice(1).toLowerCase();
}

function actionToneClass(actionLabel) {
   const label = safeText(actionLabel).toLowerCase();
   if (label === "șters" || label === "sters") return "is-action-delete";
   if (label === "creat") return "is-action-create";
   if (label === "anulat") return "is-action-cancel";
   return "is-action-update";
}

function formatGearboxLabel(value) {
   const raw = safeText(value);
   if (!raw) return "—";
   const low = raw.toLowerCase();
   if (low.startsWith("auto")) return "Automat";
   if (low.startsWith("man")) return "Manual";
   return raw;
}

function buildCardFromSnapshot(snapshot, { isDeleted = false } = {}) {
   const start = snapshot?.startISO || "";
   return {
      date: fmtDate(start),
      time: fmtTime(start),
      title: safeText(snapshot?.title) || "—",
      phone: safeText(snapshot?.phone) || "",
      sector: safeText(snapshot?.sector) || "—",
      gearbox: formatGearboxLabel(snapshot?.gearbox),
      note: safeText(snapshot?.privateMessage) || "",
      isFavorite: Boolean(snapshot?.isFavorite),
      isImportant: Boolean(snapshot?.isImportant),
      isCancelled: Boolean(snapshot?.isCancelled),
      isDeleted: Boolean(isDeleted || snapshot?.isDeleted),
      colorCss: normalizeColorToCss(snapshot?.color, "var(--event-default)"),
   };
}

function readChangedField(historyItem, field) {
   const changedField = historyItem?.changedFields?.[field];
   if (changedField && typeof changedField === "object") {
      if ("to" in changedField) return changedField.to;
      if ("value" in changedField) return changedField.value;
   }

   const changed = Array.isArray(historyItem?.changes)
      ? historyItem.changes.find((entry) => {
           const key = String(entry?.field || entry?.path || "").trim();
           return key === field;
        })
      : null;
   if (!changed) return null;
   if ("to" in changed) return changed.to;
   if ("value" in changed) return changed.value;
   return null;
}

function readHistoryItems(raw) {
   if (Array.isArray(raw)) return raw;
   if (!raw || typeof raw !== "object") return [];

   const candidates = [raw.items, raw.history, raw.data, raw.results, raw.rows, raw.list];
   for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
      if (candidate && typeof candidate === "object") {
         if (Array.isArray(candidate.items)) return candidate.items;
         if (Array.isArray(candidate.history)) return candidate.history;
         if (Array.isArray(candidate.data)) return candidate.data;
         if (Array.isArray(candidate.results)) return candidate.results;
         if (Array.isArray(candidate.rows)) return candidate.rows;
         if (Array.isArray(candidate.list)) return candidate.list;
      }
   }

   return [];
}

function readHistoryTimestamp(item) {
   return (
      item?.createdAt ||
      item?.timestamp ||
      item?.date ||
      item?.updatedAt ||
      item?.eventAt ||
      item?.time ||
      null
   );
}

function reservationIdFromHistoryItem(item) {
   if (!item || typeof item !== "object") return "";
   const rid =
      item?.reservationId ??
      item?.reservation_id ??
      item?.entityId ??
      item?.reservation?.id ??
      item?.reservation?._id ??
      item?.reservation?.reservationId ??
      item?.data?.reservation?.id ??
      null;
   return rid == null ? "" : String(rid);
}

function fullNameFromUserLike(user) {
   if (!user || typeof user !== "object") return "";
   const direct =
      safeText(user.fullName) ||
      safeText(user.name) ||
      [safeText(user.firstName), safeText(user.lastName)]
         .filter(Boolean)
         .join(" ")
         .trim();
   return direct || "";
}

function resolveActorNameFromAny(value) {
   if (value == null) return "";
   if (typeof value === "object") return fullNameFromUserLike(value);
   return safeText(value);
}

function whoFromHistory(h) {
   const objectCandidates = [
      h?.changedByUser,
      h?.changedBy,
      h?.actor,
      h?.author,
      h?.performedByUser,
      h?.performedBy,
      h?.updatedByUser,
      h?.updatedBy,
      h?.createdByUser,
      h?.createdBy,
      h?.creator,
      h?.creatorUser,
      h?.byUser,
   ];
   for (const candidate of objectCandidates) {
      const name = resolveActorNameFromAny(candidate);
      if (name) return name;
   }

   const textCandidates = [
      h?.changedByName,
      h?.actorName,
      h?.authorName,
      h?.performedByName,
      h?.updatedByName,
      h?.createdByName,
      h?.creatorName,
      h?.byName,
      h?.by,
   ];
   for (const candidate of textCandidates) {
      const name = safeText(candidate);
      if (name) return name;
   }

   return "";
}

function resolveReservationSnapshot(item, fallbackInstructorId) {
   if (!item || typeof item !== "object") return null;

   const nested =
      item?.reservation ||
      item?.reservationData ||
      item?.reservationSnapshot ||
      item?.data?.reservation ||
      null;
   const src = nested || item;

   const reservationId =
      src?.reservationId ??
      src?.reservation_id ??
      item?.reservationId ??
      item?.reservation_id ??
      src?.id ??
      src?._id ??
      item?.entityId ??
      null;
   if (reservationId == null) return null;

   const startRaw =
      src?.startTime ??
      src?.start_time ??
      src?.start ??
      src?.startAt ??
      src?.date ??
      readChangedField(item, "startTime") ??
      readChangedField(item, "start_time") ??
      readChangedField(item, "start") ??
      null;
   const startDate = parseDateFlexible(startRaw);
   if (!startDate) return null;
   const startRawParts = readRawDateTimeParts(startRaw);
   const startLocalIso = startRawParts
      ? `${startRawParts.year}-${startRawParts.month}-${startRawParts.day}T${startRawParts.hour}:${startRawParts.minute}:00`
      : buildLocalIsoTextFromDate(startDate);

   const instructorId =
      src?.instructorId ??
      src?.instructor_id ??
      src?.instructor?.id ??
      item?.instructorId ??
      item?.instructor_id ??
      fallbackInstructorId ??
      null;
   if (instructorId == null) return null;

   const action = safeText(item?.action).toUpperCase();
   const looksDeleted = DELETE_ACTIONS.has(action);

   const title =
      safeText(src?.title) ||
      safeText(src?.clientName) ||
      safeText(src?.customerName) ||
      safeText(src?.name) ||
      safeText(src?.user?.fullName) ||
      [safeText(src?.user?.firstName), safeText(src?.user?.lastName)]
         .filter(Boolean)
         .join(" ")
         .trim() ||
      "Programare";

   const instructorName =
      safeText(src?.instructorName) ||
      safeText(src?.instructor?.name) ||
      [safeText(src?.instructor?.firstName), safeText(src?.instructor?.lastName)]
         .filter(Boolean)
         .join(" ")
         .trim();

   const snapshotMs =
      toTimeMs(readHistoryTimestamp(item)) ||
      toTimeMs(src?.updatedAt) ||
      startDate.getTime();

   const isConfirmedRaw =
      src?.isConfirmed ??
      src?.is_confirmed ??
      readChangedField(item, "isConfirmed") ??
      readChangedField(item, "is_confirmed");

   return {
      id: String(reservationId),
      startISO: startLocalIso,
      startMs: startDate.getTime(),
      title,
      phone:
         safeText(src?.phone) ||
         safeText(src?.phoneNumber) ||
         safeText(src?.user?.phone) ||
         "",
      instructorId: String(instructorId),
      instructorName,
      sector:
         safeText(src?.sector) ||
         safeText(src?.sectorName) ||
         safeText(readChangedField(item, "sector")) ||
         "",
      gearbox:
         safeText(src?.gearbox) || safeText(readChangedField(item, "gearbox")) || "",
      color:
         safeText(src?.color) ||
         safeText(src?.colorToken) ||
         safeText(src?.eventColor) ||
         "",
      privateMessage:
         safeText(src?.privateMessage) ||
         safeText(src?.private_message) ||
         safeText(src?.note),
      isCancelled: Boolean(
         src?.isCancelled ??
            src?.is_cancelled ??
            src?.isCanceled ??
            src?.is_canceled ??
            false,
      ),
      isConfirmed: toBoolMaybe(isConfirmedRaw),
      isFavorite: Boolean(
         src?.isFavorite ?? src?.is_favorite ?? readChangedField(item, "isFavorite"),
      ),
      isImportant: Boolean(
         src?.isImportant ?? src?.is_important ?? readChangedField(item, "isImportant"),
      ),
      isDeleted: Boolean(
         src?.deletedAt ??
            src?.deleted_at ??
            src?.isDeleted ??
            src?.is_deleted ??
            looksDeleted,
      ),
      snapshotMs,
      raw: src,
   };
}

function buildSlotHistoryEvents(raw, instructorId, rangeFromMs, rangeToMs) {
   const normalizedInstructorId = String(instructorId || "").trim();
   if (!normalizedInstructorId) return [];
   if (
      !Number.isFinite(rangeFromMs) ||
      !Number.isFinite(rangeToMs) ||
      rangeToMs <= rangeFromMs
   ) {
      return [];
   }

   const items = readHistoryItems(raw);
   const ordered = items.slice().sort((a, b) => {
      const aa = toTimeMs(readHistoryTimestamp(a));
      const bb = toTimeMs(readHistoryTimestamp(b));
      return aa - bb;
   });

   const knownInSlot = new Map();
   const events = [];

   for (const item of ordered) {
      const action = safeText(item?.action).toUpperCase();
      const reservationId = reservationIdFromHistoryItem(item);
      const who = whoFromHistory(item) || "—";
      const when = readHistoryTimestamp(item);
      const whenMs = toTimeMs(when);
      const snapshot = resolveReservationSnapshot(item, normalizedInstructorId);
      const prevSnapshot = reservationId ? knownInSlot.get(reservationId) : null;

      const inSlotNow =
         snapshot &&
         snapshot.instructorId === normalizedInstructorId &&
         Number(snapshot.startMs) >= rangeFromMs &&
         Number(snapshot.startMs) < rangeToMs;
      const wasInSlot = Boolean(prevSnapshot);

      if (DELETE_ACTIONS.has(action)) {
         if (!reservationId || (!wasInSlot && !inSlotNow)) continue;

         const cardSource = snapshot || prevSnapshot;
         if (!cardSource) continue;

         events.push({
            id: safeText(item?.id) || `${reservationId}-delete-${whenMs}`,
            reservationId,
            actionLabel: "Șters",
            actionTone: "is-action-delete",
            time: fmtDateTimeRO(when),
            whenMs,
            by: who,
            changesSummary: "",
            card: buildCardFromSnapshot(cardSource, { isDeleted: true }),
            rawItem: item,
            rawSnapshot: cardSource?.raw || cardSource,
         });
         knownInSlot.delete(reservationId);
         continue;
      }

      if (!snapshot || snapshot.instructorId !== normalizedInstructorId) continue;
      if (!inSlotNow && !wasInSlot) continue;

      const actionLabel = formatActionLabel(action, item);
      const changesSummary =
         CREATE_ACTIONS.has(action) && !summarizeHistoryChanges(item)
            ? "Rezervare nouă"
            : summarizeHistoryChanges(item);

      events.push({
         id: safeText(item?.id) || `${snapshot.id}-${whenMs}-${events.length}`,
         reservationId: snapshot.id,
         actionLabel,
         actionTone: actionToneClass(actionLabel),
         time: fmtDateTimeRO(when),
         whenMs,
         by: who,
         changesSummary,
         card: buildCardFromSnapshot(snapshot),
         rawItem: item,
         rawSnapshot: snapshot?.raw || snapshot,
      });

      if (inSlotNow) {
         knownInSlot.set(snapshot.id, snapshot);
      } else if (wasInSlot) {
         knownInSlot.delete(snapshot.id);
      }
   }

   events.sort((a, b) => Number(b.whenMs || 0) - Number(a.whenMs || 0));
   return events;
}

function getHistoryEventKey(entry, index) {
   const id = safeText(entry?.id);
   if (id) return `event-${id}`;
   return `event-${String(entry?.reservationId || "x")}-${index}`;
}

function groupEventsByReservation(events, ascending) {
   const groups = new Map();

   for (const event of events) {
      const reservationId = safeText(event?.reservationId) || getHistoryEventKey(event, 0);
      if (!groups.has(reservationId)) groups.set(reservationId, []);
      groups.get(reservationId).push(event);
   }

   const result = Array.from(groups.entries()).map(([reservationId, groupEvents]) => {
      const sorted = groupEvents
         .slice()
         .sort((a, b) => Number(a?.whenMs || 0) - Number(b?.whenMs || 0));
      const latestEvent = sorted[sorted.length - 1] || null;

      return {
         reservationId,
         events: sorted,
         latestMs: Number(latestEvent?.whenMs || 0),
         title: safeText(latestEvent?.card?.title) || "—",
         cardColor: latestEvent?.card?.colorCss || "var(--event-default)",
      };
   });

   result.sort((a, b) => {
      const diff = Number(a.latestMs || 0) - Number(b.latestMs || 0);
      return ascending ? diff : -diff;
   });

   return result;
}

function getGroupItemPosition(index, total) {
   if (total <= 1) return "is-only";
   if (index === 0) return "is-first";
   if (index === total - 1) return "is-last";
   return "is-middle";
}

export default function UltraSlotHistoryPopup({
   instructorId = "",
   instructorName = "",
   slotLabel = "",
   slotStartISO = "",
}) {
   const [loading, setLoading] = useState(true);
   const [errorText, setErrorText] = useState("");
   const [events, setEvents] = useState([]);
   const [eventsAscending, setEventsAscending] = useState(false);

   const normalizedInstructorId = useMemo(
      () => String(instructorId || "").trim(),
      [instructorId],
   );
   const normalizedSlotStartISO = useMemo(
      () => String(slotStartISO || "").trim(),
      [slotStartISO],
   );
   const slotRange = useMemo(() => {
      const from = parseDateFlexible(normalizedSlotStartISO);
      if (!(from instanceof Date) || Number.isNaN(from.getTime())) return null;
      const to = addMinutes(from, RANGE_MINUTES);
      if (!(to instanceof Date) || Number.isNaN(to.getTime())) return null;
      return { fromMs: from.getTime(), toMs: to.getTime() };
   }, [normalizedSlotStartISO]);
   const slotDateLabel = useMemo(() => {
      if (!normalizedSlotStartISO) return "—";
      return fmtDate(normalizedSlotStartISO);
   }, [normalizedSlotStartISO]);
   const slotTimeLabel = useMemo(() => {
      const fromIso = fmtTime(normalizedSlotStartISO);
      if (fromIso && fromIso !== "—") return fromIso;
      return safeText(slotLabel) || "—";
   }, [normalizedSlotStartISO, slotLabel]);

   useEffect(() => {
      let alive = true;

      if (!normalizedInstructorId) {
         setLoading(false);
         setErrorText("Instructor invalid pentru istoricul casetei.");
         setEvents([]);
         return undefined;
      }
      if (!slotRange) {
         setLoading(false);
         setErrorText("Slot invalid pentru istoricul casetei.");
         setEvents([]);
         return undefined;
      }

      const load = async () => {
         setLoading(true);
         setErrorText("");

         try {
            const raw = await getInstructorReservationHistory(normalizedInstructorId);
            if (!alive) return;
            const normalized = buildSlotHistoryEvents(
               raw,
               normalizedInstructorId,
               slotRange.fromMs,
               slotRange.toMs,
            );
            setEvents(normalized);
         } catch (error) {
            if (!alive) return;
            setEvents([]);
            setErrorText(
               error?.message || "Nu am putut încărca istoricul pentru această casetă.",
            );
         } finally {
            if (alive) setLoading(false);
         }
      };

      void load();
      return () => {
         alive = false;
      };
   }, [normalizedInstructorId, slotRange]);

   const sortedEvents = useMemo(() => {
      const list = Array.isArray(events) ? [...events] : [];
      list.sort((a, b) => {
         const diff = Number(a?.whenMs || 0) - Number(b?.whenMs || 0);
         return eventsAscending ? diff : -diff;
      });
      return list;
   }, [events, eventsAscending]);

   const groupedEvents = useMemo(
      () => groupEventsByReservation(sortedEvents, eventsAscending),
      [sortedEvents, eventsAscending],
   );

   const totalEventCount = sortedEvents.length;
   const totalGroupCount = groupedEvents.length;

   const displayInstructorName = useMemo(() => {
      const fromProp = safeText(instructorName);
      if (fromProp) return fromProp;
      return normalizedInstructorId
         ? `Instructor #${normalizedInstructorId}`
         : "Instructor";
   }, [instructorName, normalizedInstructorId]);

   const openReservationHistory = (entry) => {
      const reservationId = safeText(entry?.reservationId);
      if (!reservationId) return;
      openSubPopup("reservationEdit", {
         reservationId,
         reservationSnapshot: entry?.rawSnapshot || null,
         initialView: "history",
      });
   };

   return (
      <div className="ultraCellHistoryPopup">
         <div className="ultraCellHistoryPopup__header">
            <h3 className="ultraCellHistoryPopup__title">Istoric pe casetă</h3>
         </div>

         <section className="ultraCellHistoryPopup__contextGroup">
            <div className="ultraCellHistoryPopup__contextRow">
               <div className="ultraCellHistoryPopup__field">
                  <span className="ultraCellHistoryPopup__label">Ziua</span>
                  <span className="ultraCellHistoryPopup__value">{slotDateLabel}</span>
               </div>
               <div className="ultraCellHistoryPopup__field">
                  <span className="ultraCellHistoryPopup__label">Instructor</span>
                  <span className="ultraCellHistoryPopup__value">
                     {displayInstructorName}
                  </span>
               </div>
               <div className="ultraCellHistoryPopup__field">
                  <span className="ultraCellHistoryPopup__label">Ora</span>
                  <span className="ultraCellHistoryPopup__value">{slotTimeLabel}</span>
               </div>
            </div>
         </section>

         {loading ? (
            <p className="ultraCellHistoryPopup__loading">Se încarcă istoricul...</p>
         ) : errorText ? (
            <p className="ultraCellHistoryPopup__error">{errorText}</p>
         ) : sortedEvents.length === 0 ? (
            <p className="ultraCellHistoryPopup__empty">
               Nu există evenimente pentru slotul selectat.
            </p>
         ) : (
            <>
               <div className="ultraCellHistoryPopup__metaBar">
                     <span className="ultraCellHistoryPopup__count">
                        {totalGroupCount}{" "}
                        {totalGroupCount === 1 ? "rezervare" : "rezervări"}
                        {totalEventCount !== totalGroupCount
                           ? ` · ${totalEventCount} evenimente`
                           : ""}
                     </span>

                     <button
                        type="button"
                        className="ultraCellHistoryPopup__sortBtn"
                        onClick={() => setEventsAscending((prev) => !prev)}
                        title={
                           eventsAscending
                              ? "Sortează descrescător"
                              : "Sortează crescător"
                        }
                        aria-label={
                           eventsAscending
                              ? "Sortează descrescător"
                              : "Sortează crescător"
                        }
                     >
                        <ReactSVG
                           src={arrowIcon}
                           className={
                              "ultraCellHistoryPopup__sortIcon" +
                              (eventsAscending ? "" : " is-desc")
                           }
                        />
                     </button>
                  </div>

                  <div className="ultraCellHistoryPopup__groups">
                        {groupedEvents.map((group) => {
                           const groupEvents = group.events || [];
                           const isMultiStage = groupEvents.length > 1;

                           return (
                              <section
                                 key={`group-${group.reservationId}`}
                                 className={[
                                    "ultraCellHistoryPopup__group",
                                    isMultiStage
                                       ? "ultraCellHistoryPopup__group--multi"
                                       : "",
                                 ]
                                    .filter(Boolean)
                                    .join(" ")}
                              >
                                 {isMultiStage ? (
                                    <header className="ultraCellHistoryPopup__groupHeader">
                                       <span className="ultraCellHistoryPopup__groupTitle">
                                          Cu {group.title}
                                       </span>
                                       <span className="ultraCellHistoryPopup__groupCount">
                                          {groupEvents.length} etape
                                       </span>
                                    </header>
                                 ) : null}

                                 <div className="ultraCellHistoryPopup__groupBody">
                                    {groupEvents.map((entry, index) => {
                                       const reservationId = safeText(entry?.reservationId);
                                       const card = entry?.card || {};
                                       const cardColor =
                                          card.colorCss || "var(--event-default)";
                                       const position = getGroupItemPosition(
                                          index,
                                          groupEvents.length,
                                       );

                                       return (
                                          <div
                                             key={getHistoryEventKey(entry, index)}
                                             className={[
                                                "ultraCellHistoryPopup__historyItem",
                                                position,
                                             ]
                                                .filter(Boolean)
                                                .join(" ")}
                                          >
                                             <div className="ultraCellHistoryPopup__meta">
                                                <div className="ultraCellHistoryPopup__metaGrid">
                                                   <span className="ultraCellHistoryPopup__metaLabel">
                                                      Acțiune
                                                   </span>
                                                   <span
                                                      className={[
                                                         "ultraCellHistoryPopup__metaValue",
                                                         "ultraCellHistoryPopup__actionValue",
                                                         entry.actionTone || "",
                                                      ]
                                                         .filter(Boolean)
                                                         .join(" ")}
                                                   >
                                                      {entry.actionLabel || "—"}
                                                   </span>

                                                   <span className="ultraCellHistoryPopup__metaLabel">
                                                      Data / ora
                                                   </span>
                                                   <span className="ultraCellHistoryPopup__metaValue">
                                                      {entry.time || "—"}
                                                   </span>

                                                   <span className="ultraCellHistoryPopup__metaLabel">
                                                      Modificat
                                                   </span>
                                                   <span className="ultraCellHistoryPopup__metaValue">
                                                      {entry.by || "—"}
                                                   </span>

                                                   {safeText(entry?.changesSummary) ? (
                                                      <>
                                                         <span className="ultraCellHistoryPopup__metaLabel">
                                                            Detalii
                                                         </span>
                                                         <span className="ultraCellHistoryPopup__metaValue">
                                                            {safeText(entry.changesSummary)}
                                                         </span>
                                                      </>
                                                   ) : null}
                                                </div>
                                             </div>

                                             <article
                                                className={[
                                                   "ultraCellHistoryPopup__reservationCard",
                                                   card.isDeleted
                                                      ? "ultraCellHistoryPopup__reservationCard--deleted"
                                                      : "",
                                                ]
                                                   .filter(Boolean)
                                                   .join(" ")}
                                                style={{ background: cardColor }}
                                                onClick={() => openReservationHistory(entry)}
                                                role={reservationId ? "button" : undefined}
                                                tabIndex={reservationId ? 0 : undefined}
                                                onKeyDown={(event) => {
                                                   if (!reservationId) return;
                                                   if (
                                                      event.key !== "Enter" &&
                                                      event.key !== " "
                                                   )
                                                      return;
                                                   event.preventDefault();
                                                   openReservationHistory(entry);
                                                }}
                                             >
                                                {card.isCancelled ? (
                                                   <span className="ultraCellHistoryPopup__reservationDot" />
                                                ) : null}

                                                {card.isDeleted ? (
                                                   <span className="ultraCellHistoryPopup__deletedBadge">
                                                      Șters
                                                   </span>
                                                ) : null}

                                                <p className="ultraCellHistoryPopup__reservationDate">
                                                   {card.date || "—"}
                                                   {card.time ? ", " : ""}
                                                   {card.time ? (
                                                      <span className="ultraCellHistoryPopup__reservationTime">
                                                         {card.time}
                                                      </span>
                                                   ) : null}
                                                </p>

                                                <p className="ultraCellHistoryPopup__reservationMeta ultraCellHistoryPopup__reservationMeta--sub">
                                                   Cu {card.title || "—"}
                                                </p>

                                                {safeText(card.phone) ? (
                                                   <p className="ultraCellHistoryPopup__reservationMeta">
                                                      {safeText(card.phone)}
                                                   </p>
                                                ) : null}

                                                <p className="ultraCellHistoryPopup__reservationMeta">
                                                   {card.sector || "—"} · {card.gearbox || "—"}
                                                   {card.isFavorite ? (
                                                      <span className="ultraCellHistoryPopup__reservationFlag">
                                                         {" "}
                                                         ⁂
                                                      </span>
                                                   ) : null}
                                                   {card.isImportant ? (
                                                      <span className="ultraCellHistoryPopup__reservationFlag">
                                                         {" "}
                                                         ‼
                                                      </span>
                                                   ) : null}
                                                </p>

                                                {safeText(card.note) ? (
                                                   <p className="ultraCellHistoryPopup__reservationMeta">
                                                      {safeText(card.note)}
                                                   </p>
                                                ) : null}
                                             </article>
                                          </div>
                                       );
                                    })}
                                 </div>
                              </section>
                           );
                        })}
                     </div>
               </>
            )}
      </div>
   );
}
